'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { createSelector } from 'reselect';
import { intersectCssClassData } from 'DesignData/lib/css';
import { isSectionDelimiterType } from 'DesignData/helpers/widgets';
import { getLayoutData, makeGetWidgetSmartObjects } from 'DesignData/selectors/layoutData';
import CssClassData from 'DesignData/models/css/CssClassData';
import { FOCUS_WIDGET_PROPERTIES_EDITOR } from 'DesignData/enums/FlashKeys';
/**
 * Required props:
 *  layoutId
 */

export const getLayoutEditorSessionById = (state, props) => {
  return state.layoutEditorSession.byId[props.layoutId] || state.layoutEditorSession.byId.default;
};
/**
 * Required props:
 *  layoutId
 */

export const getSelectedWidgets = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  return session.selectedWidgets;
};
export const getDoesSelectedWidgetsContainSectionDelimiter = createSelector([getSelectedWidgets, getLayoutData], (selectedWidgets, layoutData) => {
  if (selectedWidgets.order.length) {
    return selectedWidgets.order.some(__widgetId => {
      const widget = layoutData.widgets[__widgetId];
      return widget && widget.type === 'section_delimiter';
    });
  }

  return false;
});
/**
 * Required props:
 *  layoutId
 */

export const getIsWidgetPropertiesEditorFocusRequested = (state, props) => {
  const f = state.flash[FOCUS_WIDGET_PROPERTIES_EDITOR];
  return !!(f && f.layoutId === props.layoutId);
};
/**
 * Required props:
 *  layoutId
 * Optional props:
 *  revisionId
 */

export const makeGetSelectedWidgetsAsLayoutWidgets = () => {
  const empty = SeamlessImmutable({
    order: [],
    widgets: {}
  });
  let data = empty;
  return createSelector([getSelectedWidgets, getLayoutData], (selectedWidgets, layoutData) => {
    if (!(selectedWidgets.order.length && layoutData && layoutData.widgets)) {
      return empty;
    }

    const order = [];
    const widgets = {};
    selectedWidgets.order.forEach(id => {
      /*
       * Filter out widgets that may not be in data, like if they've been deleted.
       * We want to maintain the selections in the session reducer. For example,
       * if the user chooses to undo a delete their selections will still be there.
       */
      const widget = layoutData.widgets[id];

      if (!widget) {
        return;
      }

      order.push(id);
      widgets[id] = widget;
    });
    data = data.set('order', order);
    data = data.set('widgets', widgets);
    return data;
  });
};
/**
 * Required props:
 *  layoutId
 */

export const getRevisionMode = (state, props) => {
  const {
    revisionMode
  } = getLayoutEditorSessionById(state, props);
  return revisionMode;
};
/**
 * Required props:
 *  layoutId
 * Optional props:
 *  revisionId
 */

export const makeGetSharedCssClassDataForSelectedWidgets = () => {
  const empty = new CssClassData({
    order: [],
    classes: {}
  });
  let data = empty;
  const getSelectedWidgetsAsLayoutWidgets = makeGetSelectedWidgetsAsLayoutWidgets();
  return createSelector([getSelectedWidgetsAsLayoutWidgets], selectedLayoutWidgets => {
    if (selectedLayoutWidgets.order.length === 0) {
      data = empty;
      return data;
    }

    const classDataList = selectedLayoutWidgets.order.map(widgetId => {
      const widget = selectedLayoutWidgets.widgets[widgetId];
      return widget.getCssClassData();
    });
    const intersectedData = intersectCssClassData(classDataList);
    data = data.merge(intersectedData);
    return data;
  });
};
/**
 * Required props:
 *  layoutId
 * Optional props:
 *  revisionId
 */

export const makeGetCanSelectedWidgetsBeDeleted = () => {
  const getSelectedWidgetsAsLayoutWidgets = makeGetSelectedWidgetsAsLayoutWidgets();
  return createSelector([getSelectedWidgetsAsLayoutWidgets], ({
    order,
    widgets
  }) => {
    return order.every(id => {
      return !isSectionDelimiterType(widgets[id].type);
    });
  });
};
/**
 * Required props:
 *  layoutId
 *  widgetId
 * Optional props:
 *  revisionId
 */

export const getWidgetForWidgetContentEditor = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  const layoutData = getLayoutData(state, props);

  if (!layoutData) {
    return null;
  }

  const {
    widgetId
  } = session.widgetContentEditor;
  return layoutData.widgets[widgetId] || null;
};
export const getIsWidgetContentEditorActive = (state, props) => {
  const widgetForContentEditor = getWidgetForWidgetContentEditor(state, props);
  return !!widgetForContentEditor;
};
export const getWidgetForBlogContentTemplatesEditor = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  const layoutData = getLayoutData(state, props);

  if (!layoutData) {
    return null;
  }

  const {
    widgetId
  } = session.blogContentTemplatesEditor;
  return layoutData.widgets[widgetId] || null;
};
export const getSelectedBlogContentTemplateEditor = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  const layoutData = getLayoutData(state, props);

  if (!layoutData) {
    return null;
  }

  return session.blogContentTemplatesEditor.selectedTemplate;
};
export const getWidgetForFullScreenCodeEditor = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  const layoutData = getLayoutData(state, props);

  if (!layoutData) {
    return null;
  }

  const {
    widgetId
  } = session.fullScreenCodeEditor;
  return layoutData.widgets[widgetId] || null;
};
export const getWidgetFieldPropsFullScreenEditor = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  const {
    fieldProps
  } = session.fullScreenCodeEditor;
  return fieldProps;
};
export const getPropertyEditorFieldForFullScreenEditor = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  const {
    selectedField
  } = session.fullScreenHeadBodyEditor;
  return selectedField;
};
/**
 * Get selected smart widget rule.
 * Required Props:
 *   layoutId
 *   widgetId
 */

export const makeGetSelectedSmartRule = () => {
  const getWidgetSmartObjects = makeGetWidgetSmartObjects();
  return createSelector([getWidgetSmartObjects, getLayoutEditorSessionById], (smartObjectsById, layoutEditorSession) => {
    const {
      selectedRuleId
    } = layoutEditorSession.smartContentEditor;
    return smartObjectsById[selectedRuleId] || null;
  });
};
export const getEditingLanguage = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  return session.editingLanguage;
};
export const getEditorMode = (state, props) => {
  const session = getLayoutEditorSessionById(state, props);
  return session.editorMode;
};